// [claude-code 2026-03-22] Unified system status context — polls /api/diagnostics for per-service health

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useGateway } from "./GatewayContext";
import {
  readGrokRuntimeDiagnostics,
  type GrokRuntimeDiagnostics,
} from "../lib/grok-runtime-contract";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type StatusLevel = "ok" | "degraded" | "error" | "unknown";

export interface ServiceHealth {
  name: string;
  key: string;
  status: StatusLevel;
  detail?: string;
  fix?: string;
  lastChecked: string;
}

export interface SystemStatusValue {
  overall: StatusLevel;
  services: ServiceHealth[];
  runtime: GrokRuntimeDiagnostics | null;
  lastFullCheck: string | null;
  isChecking: boolean;
  refreshNow: () => void;
}

export const SystemStatusContext = createContext<SystemStatusValue>({
  overall: "unknown",
  services: [],
  runtime: null,
  lastFullCheck: null,
  isChecking: false,
  refreshNow: () => {},
});

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const POLL_INTERVAL_MS = 30_000;

/** Map backend service names → short display keys for the footer */
const KEY_MAP: Record<string, string> = {
  "Interactive Runtime": "runtime",
  "Grok Build": "runtime",
  "OpenAI Codex": "ai",
  Supabase: "database",
  Database: "database",
  "X Feed": "x",
  "Supabase Auth": "auth",
  Forum: "forum",
  LiveKit: "forum",
  "TradingView Econ Calendar": "econ",
  "TradingView Quotes": "quotes",
};

const SHORT_NAMES: Record<string, string> = {
  "Interactive Runtime": "Runtime",
  "Grok Build": "Runtime",
  "OpenAI Codex": "AI",
  Supabase: "Database",
  Database: "Database",
  "X Feed": "X",
  "Supabase Auth": "Auth",
  Forum: "Forum",
  LiveKit: "Forum",
  "TradingView Econ Calendar": "econ",
  "TradingView Quotes": "quotes",
};

function normalizeStatus(s: string): StatusLevel {
  if (s === "ok") return "ok";
  if (s === "degraded") return "degraded";
  if (s === "error") return "error";
  return "unknown";
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function SystemStatusProvider({ children }: { children: ReactNode }) {
  const { status: gatewayStatus, gatewayUrl } = useGateway();
  const [overall, setOverall] = useState<StatusLevel>("unknown");
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [runtime, setRuntime] = useState<GrokRuntimeDiagnostics | null>(null);
  const [lastFullCheck, setLastFullCheck] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const checkInFlightRef = useRef(false);

  const fetchDiagnostics = useCallback(async () => {
    if (gatewayStatus !== "connected") {
      setOverall("unknown");
      setRuntime(null);
      setServices((prev) =>
        prev.map((s) => ({ ...s, status: "unknown" as StatusLevel })),
      );
      return;
    }
    if (checkInFlightRef.current) return;

    checkInFlightRef.current = true;
    setIsChecking(true);
    try {
      const res = await fetch(`${gatewayUrl}/api/diagnostics`, {
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) throw new Error(`Diagnostics failed (${res.status})`);
      const data = (await res.json()) as {
        timestamp: string;
        overall: string;
        services?: Array<{
          name: string;
          status: string;
          detail?: string;
          fix?: string;
        }>;
      };

      const now = new Date().toISOString();
      const runtimeCapability = readGrokRuntimeDiagnostics(data);
      const mapped: ServiceHealth[] = (data.services ?? []).map((svc) => ({
        name: SHORT_NAMES[svc.name] ?? svc.name,
        key: KEY_MAP[svc.name] ?? svc.name.toLowerCase().replace(/\s+/g, "-"),
        status: normalizeStatus(svc.status),
        detail: svc.detail,
        fix: svc.fix,
        lastChecked: now,
      }));
      const withoutDuplicateRuntime = mapped.filter(
        (service) => service.key !== "runtime",
      );
      const runtimeService: ServiceHealth = {
        name: "Runtime",
        key: "runtime",
        status: runtimeCapability
          ? runtimeCapability.available
            ? "ok"
            : runtimeCapability.state === "degraded"
              ? "degraded"
              : "error"
          : "unknown",
        detail:
          runtimeCapability?.detail ??
          "Awaiting verified Grok Build diagnostics",
        lastChecked: now,
      };

      setServices([runtimeService, ...withoutDuplicateRuntime]);
      setRuntime(runtimeCapability);
      setOverall(normalizeStatus(data.overall));
      setLastFullCheck(now);
    } catch {
      // If diagnostics fetch fails but gateway is connected, mark as degraded
      setOverall("degraded");
      setRuntime(null);
    } finally {
      checkInFlightRef.current = false;
      setIsChecking(false);
    }
  }, [gatewayStatus, gatewayUrl]);

  // Poll on interval
  useEffect(() => {
    if (gatewayStatus !== "connected") return;

    fetchDiagnostics();
    const interval = setInterval(fetchDiagnostics, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [gatewayStatus, fetchDiagnostics]);

  return (
    <SystemStatusContext.Provider
      value={{
        overall,
        services,
        runtime,
        lastFullCheck,
        isChecking,
        refreshNow: fetchDiagnostics,
      }}
    >
      {children}
    </SystemStatusContext.Provider>
  );
}
